/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React from "react";
import { TextField, Typography, Button } from "@mui/material";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import LabelledField from "components/labelled-field";
import { LinkSelectorWrapper } from "cms/links/link-selector/link-selector-wrapper";
import { ImageSelectorWrapper } from "cms/images/image-selector/image-selector-wrapper";

const ContactCardComponentEditor = () => {
    const { component, update } = useUpdateComponent();
    
    const title = component?.title;
    const subtitle = component?.subtitle;
    const contactList = component?.contact_list || [];

    const addContactItem = () => {
        const newItem = {
            email: "",
            phone: "",
            url: "",
            link: null,
            logo: "",
            orgName: ""
        };
        const updatedList = [...contactList, newItem];
        update("contact_list", updatedList);
    };

    const removeContactItem = (index) => {
        const updatedList = contactList.filter((_, i) => i !== index);
        update("contact_list", updatedList);
    };

    const updateContactItem = (index, field, value) => {
        const updatedList = contactList.map((item, i) => 
            i === index ? { ...item, [field]: value } : item
        );
        update("contact_list", updatedList);
    };

    const handleLinkSet = (contactIndex, selectedLink) => {
        updateContactItem(contactIndex, "link", selectedLink);
    };

    const handleLinkChange = (contactIndex, selectedLink) => {
        const currentContactItem = contactList?.[contactIndex];
        const updatedContactItem = { 
            ...currentContactItem, 
            link: selectedLink, 
            url: selectedLink?.url || "" 
        };
        const updatedContactList = contactList?.map((item, itemIndex) => 
            itemIndex === contactIndex ? updatedContactItem : item
        );
        update("contact_list", updatedContactList);
    };

    const handleLinkRemove = (contactIndex) => {
        const currentContactItem = contactList?.[contactIndex];
        const updatedContactItem = { 
            ...currentContactItem, 
            link: null, 
            url: "" 
        };
        const updatedContactList = contactList?.map((item, itemIndex) => 
            itemIndex === contactIndex ? updatedContactItem : item
        );
        update("contact_list", updatedContactList);
    };

    const handleImageChange = (contactIndex, selectedImage) => {
        updateContactItem(contactIndex, "logo", selectedImage?.url || "");
    };

    const handleImageSave = (contactIndex, savedImage) => {
        updateContactItem(contactIndex, "logo", savedImage?.url || "");
    };

    const handleImageRemove = (contactIndex) => {
        updateContactItem(contactIndex, "logo", "");
    };

    const handleImageReload = (contactIndex, reloadedImage) => {
        updateContactItem(contactIndex, "logo", reloadedImage?.url || "");
    };

    return (
        <div className="simplicity-contact-card-editor">
            
            <div className="simplicity-contact-card-editor-general">
                <div className="simplicity-contact-card-editor-general-container">
                    <div className="simplicity-contact-card-editor-general-container-item">
                        <LabelledField label="Title">
                            <TextField
                                fullWidth
                                value={title || ""}
                                onChange={(event) => update("title", event.target.value)}
                                placeholder="Enter title"
                                variant="outlined"
                            />
                        </LabelledField>
                    </div>
                    
                    <div className="simplicity-contact-card-editor-general-container-item">
                        <LabelledField label="Subtitle">
                            <TextField
                                fullWidth
                                value={subtitle || ""}
                                onChange={(event) => update("subtitle", event.target.value)}
                                placeholder="Enter subtitle"
                                variant="outlined"
                            />
                        </LabelledField>
                    </div>
                </div>
            </div>
            
            <div className="simplicity-contact-card-editor-add-contact-item">
                <Button 
                    variant="outlined" 
                    onClick={addContactItem}
                    color="primary"
                >
                    Add Contact Item
                </Button>
            </div>

            {contactList.map((contactItem, index) => {
                return (
                    <div key={index} className="simplicity-contact-card-editor-contact-item">
                        <div className="simplicity-contact-card-editor-contact-item-header">
                            <Typography variant="subtitle1">
                                Contact Item {index + 1}
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                size="small"
                                onClick={() => removeContactItem(index)}
                            >
                                Remove
                            </Button>
                        </div>
                        
                        <div className="simplicity-contact-card-editor-contact-item-fields">
                            <div className="simplicity-contact-card-editor-contact-item-field">
                                <LabelledField label="Organization Name">
                                    <TextField
                                        fullWidth
                                        value={contactItem?.orgName || ""}
                                        onChange={(event) => updateContactItem(index, "orgName", event.target.value)}
                                        placeholder="Enter organization name"
                                        variant="outlined"
                                    />
                                </LabelledField>
                            </div>
                            
                            <div className="simplicity-contact-card-editor-contact-item-field">
                                <LabelledField label="Email">
                                    <TextField
                                        fullWidth
                                        value={contactItem?.email || ""}
                                        onChange={(event) => updateContactItem(index, "email", event.target.value)}
                                        placeholder="Enter email address"
                                        variant="outlined"
                                        type="email"
                                    />
                                </LabelledField>
                            </div>
                            
                            <div className="simplicity-contact-card-editor-contact-item-field">
                                <LabelledField label="Phone">
                                    <TextField
                                        fullWidth
                                        value={contactItem?.phone || ""}
                                        onChange={(event) => updateContactItem(index, "phone", event.target.value)}
                                        placeholder="Enter phone number"
                                        variant="outlined"
                                    />
                                </LabelledField>
                            </div>
                            
                            <div className="simplicity-contact-card-editor-contact-item-field">
                                <LabelledField label="URL">
                                    <LinkSelectorWrapper
                                        link={contactItem?.link}
                                        setLink={(selectedLink) => handleLinkSet(index, selectedLink)}
                                        onLinkChange={(selectedLink) => handleLinkChange(index, selectedLink)}
                                        onLinkRemove={() => handleLinkRemove(index)}
                                    />
                                </LabelledField>
                            </div>
                            
                            <div className="simplicity-contact-card-editor-contact-item-field">
                                <LabelledField label="Logo">
                                    {contactItem && (
                                        <ImageSelectorWrapper
                                            image={contactItem?.logo && contactItem.logo !== "" ? { url: contactItem.logo } : undefined}
                                            onImageChange={(selectedImage) => handleImageChange(index, selectedImage)}
                                            onImageSave={(savedImage) => handleImageSave(index, savedImage)}
                                            onImageRemove={() => handleImageRemove(index)}
                                            onReload={(reloadedImage) => handleImageReload(index, reloadedImage)}
                                        />
                                    )}
                                </LabelledField>
                            </div>
                        </div>
                    </div>
                );
            })}

            {contactList.length === 0 && (
                <div className="simplicity-contact-card-editor-empty-state">
                    <Typography variant="body2" color="text.secondary">
                        No contact items added yet. Click &quot;Add Contact Item&quot; to get started.
                    </Typography>
                </div>
            )}
        </div>
    );
};

ContactCardComponentEditor.displayName = "ContactCardComponentEditor";
export { ContactCardComponentEditor };
